import type * as React from 'react';
import { useMemo, type ReactNode } from 'react';

import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import {
  InvoiceDialogContent,
  InvoiceDialogFormShell,
  InvoiceDialogRightPane,
} from './invoice-dialog-shell';
import { InvoiceCreatePdfProvider, useCreatePdfFileState } from './invoice-create-pdf-context';
import { adjustmentDialogDescription, adjustmentDialogTitle } from './adjustment-note-dialog-copy';

import { useAdjustableParents } from '../api/use-adjustable-parents';
import type { InvoiceRow } from '../types';
import { sellNoteInvoiceNumber } from './adjustment-note-defaults';
import { AdjustmentDialogFields } from './adjustment-note-dialog-fields';
import type { AdjustmentNoteSubmitValues } from './adjustment-note-schema';
import { adjustmentNoteBlockers } from './adjustment-note-blockers';
import { useNoteValueChange, type ValueChangeState } from './adjustment-value-change';
import { TradeInvoiceDialogActions } from './trade-invoice-actions';
import { findParent, useAdjustmentForm, useWatchedNoteFields } from './use-adjustment-form';
import { useSaveAdjustmentMutation } from './use-save-adjustment-mutation';
import { useSuggestedNumberPrefill } from './use-suggested-number-prefill';

export interface AdjustmentNoteDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly side: 'buy' | 'sell';
  readonly noteType: 'Credit' | 'Debit';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly blOrderName?: string | null;
  readonly existingInvoice: InvoiceRow | null;
  readonly prefillParentId?: string;
  readonly prefillValues?: Partial<AdjustmentNoteSubmitValues>;
  // The follow-on path fixes the parent to the invoice it was launched from
  // and supplies its own header (with the note-kind chooser) in place of the
  // standard title block.
  readonly parentLocked?: boolean;
  readonly headerSlot?: ReactNode;
  readonly onSaved: () => void;
}

export function AdjustmentNoteDialog(props: AdjustmentNoteDialogProps) {
  const [pdfFile, setPdfFile] = useCreatePdfFileState(props.open);
  const existing = props.existingInvoice;
  const rightPane = (
    <InvoiceDialogRightPane
      existingInvoice={existing}
      file={pdfFile}
      onFileChange={setPdfFile}
      side={props.side}
    />
  );
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <InvoiceDialogContent>
        <InvoiceCreatePdfProvider value={{ file: pdfFile }}>
          {/* Keyed on the note kind so a mid-dialog kind switch (the follow-on
              chooser) rebuilds the form with that kind's defaults. */}
          {props.open && <DialogBody key={props.noteType} {...props} rightPane={rightPane} />}
        </InvoiceCreatePdfProvider>
      </InvoiceDialogContent>
    </Dialog>
  );
}

interface DialogBodyProps extends AdjustmentNoteDialogProps {
  readonly rightPane: ReactNode;
}

function DialogBody(props: DialogBodyProps) {
  const direction = props.side === 'buy' ? 'Purchase' : 'Sale';
  const parentsQuery = useAdjustableParents(props.blOrderId, direction);
  const parents = useMemo<InvoiceRow[]>(() => parentsQuery.data ?? [], [parentsQuery.data]);
  // A locked parent seeds the form defaults (currency, counterparty) from the
  // parent row, so the form can only mount once the parents have loaded.
  if (props.parentLocked === true && parentsQuery.isLoading) {
    return (
      <>
        {props.headerSlot}
        <p className="text-muted-foreground text-sm">Loading…</p>
      </>
    );
  }
  return <DialogFormInner {...props} parents={parents} parentsLoading={parentsQuery.isLoading} />;
}

interface InnerProps extends DialogBodyProps {
  readonly parents: InvoiceRow[];
  readonly parentsLoading: boolean;
}

function buildFormArgs(props: InnerProps) {
  return {
    open: props.open,
    noteType: props.noteType,
    side: props.side,
    parents: props.parents,
    existingInvoice: props.existingInvoice,
    ...(props.blOrderName !== undefined ? { blOrderName: props.blOrderName } : {}),
    ...(props.prefillParentId !== undefined ? { prefillParentId: props.prefillParentId } : {}),
    ...(props.prefillValues !== undefined ? { prefillValues: props.prefillValues } : {}),
  };
}

function useNoteNumberPrefill(form: ReturnType<typeof useAdjustmentForm>, props: InnerProps) {
  useSuggestedNumberPrefill(
    form,
    'invoice_number',
    {
      invoiceType: 'Adjustment',
      noteType: props.noteType,
      direction: props.side === 'buy' ? 'Purchase' : 'Sale',
      blOrderId: props.blOrderId,
      enabled: props.open && props.existingInvoice === null,
    },
    // The sell side pre-seeds its hidden number field with the legacy fallback;
    // an untouched field still holding it upgrades to the suggestion.
    props.side === 'sell'
      ? sellNoteInvoiceNumber(props.noteType, props.blOrderName ?? null)
      : undefined,
  );
}

function DialogFormInner(props: InnerProps) {
  const form = useAdjustmentForm(buildFormArgs(props));
  useNoteNumberPrefill(form, props);
  const watched = useWatchedNoteFields(form);
  const valueChange = useNoteValueChange({
    form,
    blOrderId: props.blOrderId,
    side: props.side,
    noteType: props.noteType,
  });
  const mutation = useSaveAdjustmentMutation({
    side: props.side,
    orderId: props.orderId,
    blOrderId: props.blOrderId,
    noteType: props.noteType,
    existingInvoice: props.existingInvoice,
    onSaved: props.onSaved,
    onOpenChange: props.onOpenChange,
  });
  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(values);
  });
  const blockers = adjustmentNoteBlockers(
    {
      noteType: props.noteType,
      side: props.side,
      ...watched,
      parentsLoading: props.parentsLoading,
      parents: props.parents,
      parentLocked: props.parentLocked === true,
    },
    valueChange,
  );
  return (
    <DialogForm
      form={form}
      props={props}
      onSubmit={onSubmit}
      saveDisabled={mutation.isPending || blockers.length > 0}
      blockers={blockers}
      isPending={mutation.isPending}
      currencyLocked={findParent(props.parents, watched.selectedId) !== null}
      valueChange={valueChange}
    />
  );
}

interface DialogFormProps {
  readonly form: ReturnType<typeof useAdjustmentForm>;
  readonly props: InnerProps;
  readonly onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  readonly saveDisabled: boolean;
  readonly blockers: readonly string[];
  readonly isPending: boolean;
  readonly currencyLocked: boolean;
  readonly valueChange: ValueChangeState;
}

function DialogForm({
  form,
  props,
  onSubmit,
  saveDisabled,
  blockers,
  isPending,
  currencyLocked,
  valueChange,
}: DialogFormProps) {
  const existing = props.existingInvoice;
  return (
    <form
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      noValidate
      className="flex min-h-0 flex-1 flex-col"
    >
      <InvoiceDialogFormShell
        rightPane={props.rightPane}
        footer={
          <TradeInvoiceDialogActions
            isPending={isPending}
            saveDisabled={saveDisabled}
            blockers={blockers}
            submitLabel={existing !== null ? 'Save Changes' : `Create ${props.noteType} Note`}
            onCancel={() => {
              props.onOpenChange(false);
            }}
          />
        }
      >
        <DialogFormBody
          form={form}
          props={props}
          isPending={isPending}
          currencyLocked={currencyLocked}
          valueChange={valueChange}
        />
      </InvoiceDialogFormShell>
    </form>
  );
}

function DialogFormBody(args: Omit<DialogFormProps, 'onSubmit' | 'saveDisabled' | 'blockers'>) {
  const { form, props, isPending, currencyLocked, valueChange } = args;
  return (
    <>
      {props.headerSlot ?? (
        <AdjustmentTitleBlock
          noteType={props.noteType}
          side={props.side}
          editing={props.existingInvoice !== null}
        />
      )}
      <AdjustmentDialogFields
        form={form}
        side={props.side}
        noteType={props.noteType}
        parents={props.parents}
        parentsLoading={props.parentsLoading}
        existingInvoice={props.existingInvoice}
        isPending={isPending}
        currencyLocked={currencyLocked}
        valueChange={valueChange}
        parentLocked={props.parentLocked === true}
      />
    </>
  );
}

function AdjustmentTitleBlock(props: {
  readonly noteType: 'Credit' | 'Debit';
  readonly side: 'buy' | 'sell';
  readonly editing: boolean;
}) {
  return (
    <DialogHeader>
      <DialogTitle>{adjustmentDialogTitle(props.noteType, props.side, props.editing)}</DialogTitle>
      <DialogDescription>{adjustmentDialogDescription(props.editing)}</DialogDescription>
    </DialogHeader>
  );
}
